import { useEffect, useRef, useState } from 'react';

const useFunctionWindows = () => {
  const [functionWindows, setFunctionWindows] = useState([]);
  const [draggingWindowId, setDraggingWindowId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizingWindowId, setResizingWindowId] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
  const [resizeDirection, setResizeDirection] = useState('');
  const functionWindowEditorRefs = useRef({});

  useEffect(() => {
    if (!draggingWindowId) return;

    const handleMouseMove = (e) => {
      setFunctionWindows((prev) =>
        prev.map((w) => {
          if (w.id !== draggingWindowId) return w;

          const newX = e.clientX - dragOffset.x;
          const newY = e.clientY - dragOffset.y;
          const windowWidth = w.isMinimized ? 400 : w.width || 800;
          const windowHeight = w.isMinimized ? 40 : w.height || 600;

          const clampedX = Math.max(0, Math.min(newX, window.innerWidth - windowWidth));
          const clampedY = Math.max(0, Math.min(newY, window.innerHeight - windowHeight));

          return {
            ...w,
            position: {
              x: clampedX,
              y: clampedY,
            },
          };
        }),
      );
    };

    const handleMouseUp = () => {
      setDraggingWindowId(null);
      setDragOffset({ x: 0, y: 0 });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingWindowId, dragOffset]);

  useEffect(() => {
    if (!resizingWindowId) return;

    const handleMouseMove = (e) => {
      setFunctionWindows((prev) =>
        prev.map((w) => {
          if (w.id !== resizingWindowId || w.isMinimized) return w;

          const deltaX = e.clientX - resizeStart.x;
          const deltaY = e.clientY - resizeStart.y;

          let newWidth = resizeStart.width;
          let newHeight = resizeStart.height;
          let newX = resizeStart.startX;
          let newY = resizeStart.startY;

          if (resizeDirection.includes('e')) {
            newWidth = Math.max(400, Math.min(2000, resizeStart.width + deltaX));
          }
          if (resizeDirection.includes('w')) {
            newWidth = Math.max(400, Math.min(2000, resizeStart.width - deltaX));
            newX = resizeStart.startX + deltaX;
          }
          if (resizeDirection.includes('s')) {
            newHeight = Math.max(300, Math.min(1500, resizeStart.height + deltaY));
          }
          if (resizeDirection.includes('n')) {
            newHeight = Math.max(300, Math.min(1500, resizeStart.height - deltaY));
            newY = resizeStart.startY + deltaY;
          }

          const clampedX = Math.max(0, Math.min(newX, window.innerWidth - newWidth));
          const clampedY = Math.max(0, Math.min(newY, window.innerHeight - newHeight));
          const clampedWidth = Math.min(newWidth, window.innerWidth - clampedX);
          const clampedHeight = Math.min(newHeight, window.innerHeight - clampedY);

          return {
            ...w,
            width: clampedWidth,
            height: clampedHeight,
            position: {
              x: clampedX,
              y: clampedY,
            },
          };
        }),
      );
    };

    const handleMouseUp = () => {
      setResizingWindowId(null);
      setResizeStart({ x: 0, y: 0, width: 0, height: 0, startX: 0, startY: 0 });
      setResizeDirection('');
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingWindowId, resizeStart, resizeDirection]);

  return {
    functionWindows,
    setFunctionWindows,
    draggingWindowId,
    setDraggingWindowId,
    dragOffset,
    setDragOffset,
    resizingWindowId,
    setResizingWindowId,
    resizeStart,
    setResizeStart,
    resizeDirection,
    setResizeDirection,
    functionWindowEditorRefs,
  };
};

export default useFunctionWindows;
